import { Document, View, Text } from '@react-pdf/renderer';
import dayjs from 'dayjs';
import { PaymentTerms, Supplier } from '../../../common';
import { BillingCycleUnit, PURCHASE_ORDER_DOCUMENT_TYPE, PurchaseOrderDocumentType } from '../../../pricing';
import { PageComponent } from '../../components';
import { en } from '../../theme/languages/en';
import { DATE_FORMAT_SLASH } from '../../utils/invoiceTable';
import { styles } from './PurchaseOrderPDF.module';

// Types for Purchase Order data
export interface POLineItemData {
  name: string;
  description?: string;
  quantity: number;
  unitPrice: number;
  total: number;
  billingStartDate?: Date | null;
  billingEndDate?: Date | null;
  duration?: string;
  billingCycleUnit?: BillingCycleUnit;
}

export interface POTotalsData {
  subTotal: number;
  discount: number;
  tax: number;
  total: number;
}

export interface POProductTerms {
  productName: string;
  netTerms?: number | PaymentTerms;
  billingCycleUnit?: BillingCycleUnit;
}

export interface POPaymentTermsData {
  isIdentical: boolean;
  netTerms?: number | PaymentTerms;
  billingCycleUnit?: BillingCycleUnit;
  productTerms: POProductTerms[];
}

export interface POPartyData {
  name?: string;
  legalName?: string;
  billingAddress?: string;
  ein?: string;
  iconData?: string;
  email?: string;
}

export interface PurchaseOrderData {
  accountingEventType?: PurchaseOrderDocumentType;
  poNumber?: string;
  contractNumber?: number;
  issueDate?: Date;
  buyer?: POPartyData;
  seller?: POPartyData;
  activationStartDate?: Date | null;
  activationEndDate?: Date | null;
  currencySign: string;
  lineItems: POLineItemData[];
  totals: POTotalsData;
  paymentTerms: POPaymentTermsData;
}

interface PurchaseOrderPDFProps {
  purchaseOrderData: PurchaseOrderData;
  supplier?: Supplier;
  isPreview?: boolean;
}

const billingCycleLabels: Record<BillingCycleUnit, string> = {
  [BillingCycleUnit.ONE_TIME]: 'One Time',
  [BillingCycleUnit.WEEKLY]: 'Weekly',
  [BillingCycleUnit.MONTHLY]: 'Monthly',
  [BillingCycleUnit.QUARTERLY]: 'Quarterly',
  [BillingCycleUnit.YEARLY]: 'Yearly',
  [BillingCycleUnit.CUSTOM]: 'Custom',
};

const billingCycleShortLabels: Record<BillingCycleUnit, string> = {
  [BillingCycleUnit.ONE_TIME]: '',
  [BillingCycleUnit.WEEKLY]: 'wk.',
  [BillingCycleUnit.MONTHLY]: 'mo.',
  [BillingCycleUnit.QUARTERLY]: 'qtr.',
  [BillingCycleUnit.YEARLY]: 'yr.',
  [BillingCycleUnit.CUSTOM]: '',
};

const getPaymentTermsLabel = (netTerms: number | PaymentTerms): string => {
  if (netTerms === PaymentTerms.DUE_UPON_RECEIPT) {
    return en.dueUponReceipt;
  }
  if (netTerms === PaymentTerms.DUE_UPON_USAGE) {
    return en.dueUponUsage;
  }
  return `${en.net} ${netTerms}`;
};

const formatPrice = (value: number | undefined, currencySign: string): string => {
  if (value === undefined || value === null) {
    return '-';
  }
  return `${currencySign}${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatPriceWithCycle = (
  value: number | undefined,
  currencySign: string,
  billingCycleUnit?: BillingCycleUnit,
): string => {
  if (value === undefined || value === null || value === 0) {
    return '-';
  }
  const formatted = formatPrice(value, currencySign);
  if (
    !billingCycleUnit ||
    billingCycleUnit === BillingCycleUnit.ONE_TIME ||
    billingCycleUnit === BillingCycleUnit.CUSTOM
  ) {
    return formatted;
  }
  return `${formatted} / ${billingCycleShortLabels[billingCycleUnit]}`;
};

export const PurchaseOrderPDF = ({ purchaseOrderData, supplier, isPreview }: PurchaseOrderPDFProps) => {
  const {
    poNumber,
    contractNumber,
    issueDate,
    buyer,
    seller,
    activationStartDate,
    activationEndDate,
    currencySign,
    lineItems,
    totals,
    paymentTerms,
  } = purchaseOrderData;

  const poTitle = poNumber || (contractNumber ? `#${contractNumber}` : '-');

  // Use provided supplier or derive from seller data
  const effectiveSupplier: Supplier | undefined = supplier || (seller ? {
    name: seller.name || '',
    legalName: seller.legalName || '',
    email: seller.email,
    ein: seller.ein,
    iconData: seller.iconData,
    billingAddress: seller.billingAddress,
  } : undefined);

  return (
    <Document>
      <PageComponent supplier={effectiveSupplier} isPreview={isPreview}>
        <View style={styles.container}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.mainTitle}>Purchase Order</Text>
            <View style={styles.headerRow}>
              <View style={styles.headerItem}>
                <Text style={styles.headerLabel}>PO Number: </Text>
                <Text style={styles.headerValue}>{poTitle}</Text>
              </View>
              <View style={styles.headerItem}>
                <Text style={styles.headerLabel}>Issue Date: </Text>
                <Text style={styles.headerValue}>{issueDate ? dayjs(issueDate).format(DATE_FORMAT_SLASH) : '-'}</Text>
              </View>
            </View>
          </View>

          {/* Parties */}
          <View style={styles.partiesSection}>
            <View style={styles.partyBox}>
              <Text style={styles.partyTitle}>Buyer</Text>
              <Text style={styles.partyName}>{buyer?.name || '-'}</Text>
              {buyer?.legalName && <Text style={styles.partyText}>{buyer.legalName}</Text>}
              {buyer?.billingAddress && <Text style={styles.partyText}>{buyer.billingAddress}</Text>}
              {buyer?.ein && <Text style={styles.partyText}>Tax ID: {buyer.ein}</Text>}
            </View>

            <View style={styles.partyBox}>
              <Text style={styles.partyTitle}>Seller</Text>
              <Text style={styles.partyName}>{seller?.name || '-'}</Text>
              {seller?.legalName && <Text style={styles.partyText}>{seller.legalName}</Text>}
              {seller?.billingAddress && <Text style={styles.partyText}>{seller.billingAddress}</Text>}
              {seller?.ein && <Text style={styles.partyText}>Tax ID: {seller.ein}</Text>}
            </View>
          </View>

          {/* Contract Period */}
          <View style={styles.periodSection}>
            <Text style={styles.sectionTitle}>Contract Period</Text>
            <Text style={styles.periodValue}>
              {activationStartDate ? dayjs(activationStartDate).format(DATE_FORMAT_SLASH) : '-'}
              {activationEndDate && ` - ${dayjs(activationEndDate).format(DATE_FORMAT_SLASH)}`}
            </Text>
          </View>

          {/* Line Items Table */}
          <View style={styles.itemsSection}>
            <Text style={styles.sectionTitle}>Order Items</Text>

            {/* Table Header */}
            <View style={styles.tableHeader}>
              <View style={styles.colProduct}>
                <Text style={styles.tableHeaderText}>Products</Text>
              </View>
              <View style={styles.colPeriod}>
                <Text style={styles.tableHeaderText}>Period</Text>
              </View>
              <View style={styles.colDuration}>
                <Text style={styles.tableHeaderText}>Duration</Text>
              </View>
              <View style={styles.colQty}>
                <Text style={styles.tableHeaderText}>Qty</Text>
              </View>
              <View style={styles.colPrice}>
                <Text style={styles.tableHeaderText}>Unit Price</Text>
              </View>
              <View style={styles.colTotal}>
                <Text style={styles.tableHeaderText}>Total</Text>
              </View>
            </View>

            {/* Table Rows */}
            {lineItems.map((item, index) => (
              <View key={index} style={styles.tableRow} wrap={false}>
                <View style={styles.colProduct}>
                  <Text style={styles.tableCellText}>{item.name}</Text>
                  {item.description && <Text style={styles.tableCellTextSmall}>{item.description}</Text>}
                </View>
                <View style={styles.colPeriod}>
                  <Text style={styles.tableCellText}>
                    {item.billingStartDate
                      ? `${dayjs(item.billingStartDate).format(DATE_FORMAT_SLASH)}${
                          item.billingEndDate ? ` - ${dayjs(item.billingEndDate).format(DATE_FORMAT_SLASH)}` : ''
                        }`
                      : '-'}
                  </Text>
                </View>
                <View style={styles.colDuration}>
                  <Text style={styles.tableCellText}>{item.duration || '-'}</Text>
                </View>
                <View style={styles.colQty}>
                  <Text style={styles.tableCellText}>{item.quantity.toLocaleString()}</Text>
                </View>
                <View style={styles.colPrice}>
                  <Text style={styles.tableCellText}>
                    {formatPriceWithCycle(item.unitPrice, currencySign, item.billingCycleUnit)}
                  </Text>
                </View>
                <View style={styles.colTotal}>
                  <Text style={styles.tableCellText}>
                    {formatPriceWithCycle(item.total, currencySign, item.billingCycleUnit)}
                  </Text>
                </View>
              </View>
            ))}
          </View>

          {/* Totals */}
          <View style={styles.totalsSection} wrap={false}>
            <View style={styles.totalsBox}>
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>{en.subTotal}</Text>
                <Text style={styles.totalValue}>{formatPrice(totals.subTotal, currencySign)}</Text>
              </View>
              {totals.discount > 0 && (
                <View style={styles.totalRow}>
                  <Text style={styles.totalLabel}>{en.discount}</Text>
                  <Text style={styles.totalValue}>({formatPrice(totals.discount, currencySign)})</Text>
                </View>
              )}
              {totals.tax > 0 && (
                <View style={styles.totalRow}>
                  <Text style={styles.totalLabel}>{en.tax}</Text>
                  <Text style={styles.totalValue}>{formatPrice(totals.tax, currencySign)}</Text>
                </View>
              )}
              <View style={styles.grandTotalRow}>
                <Text style={styles.grandTotalLabel}>{en.total}</Text>
                <Text style={styles.grandTotalValue}>{formatPrice(totals.total, currencySign)}</Text>
              </View>
            </View>
          </View>

          {/* Payment Terms */}
          <View style={styles.termsSection} wrap={false}>
            <Text style={styles.sectionTitle}>Payment Terms</Text>
            {paymentTerms.isIdentical ? (
              <View style={styles.termsContent}>
                {paymentTerms.netTerms !== undefined && (
                  <View style={styles.termRow}>
                    <Text style={styles.termLabel}>Net Terms:</Text>
                    <Text style={styles.termValue}>{getPaymentTermsLabel(paymentTerms.netTerms)}</Text>
                  </View>
                )}
                {paymentTerms.billingCycleUnit && (
                  <View style={styles.termRow}>
                    <Text style={styles.termLabel}>Billing Cycle:</Text>
                    <Text style={styles.termValue}>{billingCycleLabels[paymentTerms.billingCycleUnit]}</Text>
                  </View>
                )}
              </View>
            ) : (
              <View style={styles.productTermsContainer}>
                {paymentTerms.productTerms.map((product, index) => (
                  <View key={index} style={styles.productTermsBox}>
                    <Text style={styles.productTermsName}>{product.productName}</Text>
                    <View style={styles.termsContent}>
                      {product.netTerms !== undefined && (
                        <View style={styles.termRow}>
                          <Text style={styles.termLabel}>Net Terms:</Text>
                          <Text style={styles.termValue}>{getPaymentTermsLabel(product.netTerms)}</Text>
                        </View>
                      )}
                      {product.billingCycleUnit && (
                        <View style={styles.termRow}>
                          <Text style={styles.termLabel}>Billing Cycle:</Text>
                          <Text style={styles.termValue}>{billingCycleLabels[product.billingCycleUnit]}</Text>
                        </View>
                      )}
                    </View>
                  </View>
                ))}
              </View>
            )}
          </View>
        </View>
      </PageComponent>
    </Document>
  );
};
